import { useEffect, useState } from "react";
import { Combobox } from "@headlessui/react";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Searchable customer dropdown for the Line Item Report.
 *
 * Type to search by name or customer number; pick one, and the report filters
 * on that customer's id. `value` is { id, cid, name } or null, where `id` is
 * customers.id (what WorkOrders.customerId holds) and `cid` the per-company
 * customer number shown as C-123.
 *
 * Results come from the existing /customers/search-pagination endpoint, 20 at
 * a time; with nothing typed it lists the most recently added customers.
 */
const LIMIT = 20;

export default function CustomerPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await API.post(
          "/customers/search-pagination",
          {
            page: 1,
            limit: LIMIT,
            query: query.trim() || null,
            searchFields: ["customerName", "customerId"],
          },
          { headers: authHeader() },
        );
        if (!cancelled) {
          setOptions(
            (data?.success ? data.workOrders || [] : []).map((c) => ({
              id: c.id,
              cid: c.customerId,
              name: c.customerName,
            })),
          );
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const clear = () => {
    onChange(null);
    setQuery("");
  };

  return (
    <Combobox value={value} onChange={onChange} by="id" nullable>
      <div className="position-relative">
        <Combobox.Input
          className="form-control"
          style={{ paddingRight: value ? "4rem" : "2.25rem" }}
          placeholder="All customers"
          aria-label="Customer"
          autoComplete="off"
          displayValue={(c) => (c ? c.name : "")}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="position-absolute top-50 end-0 translate-middle-y d-flex align-items-center pe-1">
          {value && (
            <button
              type="button"
              className="btn btn-sm btn-link text-muted p-1"
              aria-label="Clear customer"
              onClick={clear}
            >
              <i className="mdi mdi-close"></i>
            </button>
          )}
          <Combobox.Button className="btn btn-sm btn-link text-muted p-1" aria-label="Show customers">
            <i className="mdi mdi-chevron-down"></i>
          </Combobox.Button>
        </div>

        <Combobox.Options
          className="dropdown-menu show w-100 shadow mt-1 py-1"
          style={{ maxHeight: 300, overflowY: "auto" }}
        >
          {options.length === 0 ? (
            <div className="dropdown-item-text text-muted small">
              {loading ? "Searching..." : "No customers found"}
            </div>
          ) : (
            options.map((c) => (
              <Combobox.Option
                key={c.id}
                value={c}
                className={({ active, selected }) =>
                  `dropdown-item text-wrap ${active ? "active" : ""} ${selected ? "fw-semibold" : ""}`
                }
              >
                <span className="me-2 opacity-75">C-{c.cid}</span>
                {c.name}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}
